/**
 * Hero counters.
 *
 * Ported from `animateValue` / `easeOutCubic` (`public/index.html:709-733`) and
 * the observer that starts them once (`:735-756`): 2000 ms, threshold 0.5, one
 * decimal place when the suffix contains `M`.
 */
import { useEffect, useRef, useState } from 'react';

export interface CountUpStat {
  target: number;
  suffix: string;
  label: string;
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const format = (value: number, suffix: string) =>
  suffix
    ? value.toFixed(suffix.includes('M') ? 1 : 0) + suffix
    : `${Math.floor(value)}${suffix}`;

export const useCountUp = (stats: CountUpStat[], duration = 2000) => {
  const ref = useRef<HTMLElement | null>(null);
  const [values, setValues] = useState<string[]>(() => stats.map(() => '0'));
  const started = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || started.current) return;
          started.current = true;

          let startTimestamp: number | null = null;
          const step = (timestamp: number) => {
            if (startTimestamp === null) startTimestamp = timestamp;
            const progress = Math.min(
              (timestamp - startTimestamp) / duration,
              1
            );
            const eased = easeOutCubic(progress);
            setValues(
              stats.map((s) =>
                progress < 1
                  ? format(s.target * eased, s.suffix)
                  : s.target.toFixed(s.suffix.includes('M') ? 1 : 0) + s.suffix
              )
            );
            if (progress < 1) window.requestAnimationFrame(step);
          };
          window.requestAnimationFrame(step);
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [stats, duration]);

  return { ref, values };
};
